import { useEffect, useState } from 'react';
import { Screen } from '../components/AppShell';
import { Field } from '../components/ui';
import { api, setToken } from '../lib/api';
import { useAction } from '../lib/hooks';
import { useAuth } from '../store/auth';
import './screens.css';

export default function Settings() {
  const tenant = useAuth((s) => s.tenant);
  const user = useAuth((s) => s.user);
  const refreshTenant = useAuth((s) => s.refreshTenant);
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  const org = useAction();
  const password = useAction();

  const [form, setForm] = useState({
    name: '',
    orgName: '',
    address: '',
    postcode: '',
    phone: '',
    email: '',
    vatNumber: '',
    defaultLocation: '',
    invoicePrefix: 'INV',
    vatRate: '20',
    labourRate: '0',
  });
  const [savedOrg, setSavedOrg] = useState(false);

  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [savedPw, setSavedPw] = useState(false);

  useEffect(() => {
    if (!tenant) return;
    setForm({
      name: tenant.name ?? '',
      orgName: tenant.orgName ?? '',
      address: tenant.address ?? '',
      postcode: tenant.postcode ?? '',
      phone: tenant.phone ?? '',
      email: tenant.email ?? '',
      vatNumber: tenant.vatNumber ?? '',
      defaultLocation: tenant.defaultLocation ?? '',
      invoicePrefix: tenant.invoicePrefix ?? 'INV',
      vatRate: String(tenant.vatRate ?? 20),
      labourRate: String(tenant.labourRate ?? 0),
    });
  }, [tenant]);

  const set =
    (key: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSavedOrg(false);
      setForm((f) => ({ ...f, [key]: e.target.value }));
    };

  const saveOrg = (e: React.FormEvent) => {
    e.preventDefault();
    org.run(async () => {
      await api.patch('/auth/tenant', {
        ...form,
        vatRate: Number(form.vatRate) || 0,
        labourRate: Number(form.labourRate) || 0,
      });
      await refreshTenant();
      setSavedOrg(true);
    });
  };

  const changePassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) {
      password.setError('The new passwords do not match.');
      return;
    }
    password.run(async () => {
      const result = await api.post<{ token: string }>('/auth/change-password', {
        currentPassword: pw.current,
        newPassword: pw.next,
      });
      setToken(result.token);
      setPw({ current: '', next: '', confirm: '' });
      setSavedPw(true);
    });
  };

  return (
    <Screen title="Settings" back="/more" hideTabs>
      <div className="stack-5">
        {isAdmin && (
          <form className="stack-4" onSubmit={saveOrg}>
            <h2>Workshop details</h2>
            <p className="hint">
              These appear on inspection records and invoices. The organisation name is printed as the
              inspection organisation.
            </p>

            {org.error ? <div className="notice notice-danger">{org.error}</div> : null}
            {savedOrg ? <div className="notice notice-ok">Saved.</div> : null}

            <Field label="Trading name">
              <input className="input" value={form.name} onChange={set('name')} />
            </Field>
            <Field label="Inspection organisation">
              <input className="input" value={form.orgName} onChange={set('orgName')} />
            </Field>
            <Field label="Address">
              <textarea className="textarea" rows={2} value={form.address} onChange={set('address')} />
            </Field>
            <div className="grid-2 collapse">
              <Field label="Postcode">
                <input className="input input-mono" value={form.postcode} onChange={set('postcode')} />
              </Field>
              <Field label="Phone">
                <input className="input" type="tel" value={form.phone} onChange={set('phone')} />
              </Field>
            </div>
            <Field label="Email">
              <input className="input" type="email" autoCapitalize="off" value={form.email} onChange={set('email')} />
            </Field>
            <Field label="Default inspection location" hint="Offered when starting a new inspection.">
              <input className="input" value={form.defaultLocation} onChange={set('defaultLocation')} />
            </Field>

            <h3 style={{ marginTop: 'var(--s3)' }}>Billing</h3>
            <div className="grid-2 collapse">
              <Field label="VAT number">
                <input className="input input-mono" value={form.vatNumber} onChange={set('vatNumber')} />
              </Field>
              <Field label="VAT rate (%)">
                <input
                  className="input"
                  type="number"
                  step="0.5"
                  inputMode="decimal"
                  value={form.vatRate}
                  onChange={set('vatRate')}
                />
              </Field>
            </div>
            <div className="grid-2 collapse">
              <Field label="Labour rate (£/hr)">
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={form.labourRate}
                  onChange={set('labourRate')}
                />
              </Field>
              <Field label="Invoice prefix">
                <input className="input input-mono" value={form.invoicePrefix} onChange={set('invoicePrefix')} />
              </Field>
            </div>

            <button className="btn btn-primary" type="submit" disabled={org.busy}>
              {org.busy ? 'Saving…' : 'Save workshop details'}
            </button>
          </form>
        )}

        <form className="stack-4" onSubmit={changePassword}>
          <h2>Your password</h2>
          <p className="hint">Changing your password signs you out of every other device.</p>

          {password.error ? <div className="notice notice-danger">{password.error}</div> : null}
          {savedPw ? <div className="notice notice-ok">Password changed.</div> : null}

          <Field label="Current password">
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              value={pw.current}
              onChange={(e) => {
                setSavedPw(false);
                setPw((p) => ({ ...p, current: e.target.value }));
              }}
            />
          </Field>
          <Field label="New password" hint="At least 10 characters.">
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={pw.next}
              onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
            />
          </Field>
          <Field label="Confirm new password">
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={pw.confirm}
              onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
            />
          </Field>

          <button
            className="btn btn-primary"
            type="submit"
            disabled={password.busy || !pw.current || pw.next.length < 10}
          >
            {password.busy ? 'Changing…' : 'Change password'}
          </button>
        </form>
      </div>
    </Screen>
  );
}
